import { cx } from '@/lib/cx';
import { formatDecimalString } from '@/lib/format';

/**
 * A money figure as the API sent it: the exact decimal string, grouped, followed
 * by its currency code in a smaller muted span — `12,500.00 GBP`.
 *
 * ⚠️ It never parses to a float — `formatDecimalString` groups the text and
 * copies the fraction through — and it never converts or sums. `null` /
 * `undefined` renders `—`, never `0.00`: unpriced and free are different facts.
 * The code is omitted when there is none, so a bare figure is never given a
 * currency it did not carry.
 */
export function Money({
  amount,
  currency,
  className,
  muted = false,
}: {
  amount: string | null | undefined;
  currency?: string | null;
  className?: string;
  muted?: boolean;
}) {
  const text = formatDecimalString(amount ?? null);
  const code = currency?.trim();
  return (
    <span className={cx('tabular-nums whitespace-nowrap', muted ? 'text-fg-muted' : undefined, className)}>
      {text}
      {text !== '—' && code ? <span className="ml-1 text-[0.85em] text-fg-muted">{code}</span> : null}
    </span>
  );
}

/** The same figure as one plain string, for a `title` or an `aria-label`. */
export function formatMoneyText(amount: string | null | undefined, currency?: string | null): string {
  const text = formatDecimalString(amount ?? null);
  const code = currency?.trim();
  return text !== '—' && code ? `${text} ${code}` : text;
}
